import React, { useState } from 'react';
import { Divider } from 'antd';
import { Button } from 'modules/v2/common/AtomicDesign/atoms';
import TemplateCard from '../shared-components/TemplateCard';
import { getInstance as getDSInstance } from 'modules/api/digitalServices/instance';
import { useDesignHuddle } from 'modules/v2/pages/TemplatesBuilder/DesignHuddleContext';
import ExportProgressModal from '../components/ExportProgressModal';
import TemplateSelectionModal from '../components/TemplateSelectionModal';

const formatMap = {
  'digital': 'png',
  'print': 'png',
  'presentation': 'pdf',
  'video': 'mp4',
};

const DefaultTemplates = ({
  filteredTemplates,
  searchQuery,
  setSearchQuery,
  selectedSecondaryCategory,
  foundSecondaryCategory,
  handleSelectSecondaryCategory,
  // toggleAllSecondaryCategories,
  resetSelectedFilters,
  // currentCategory,
  onCustomize,
  onPreview
}) => {
  const DSInstance = getDSInstance();
  const { userProfile, userCustomizationsHash } = useDesignHuddle();
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [exportJobs, setExportJobs] = useState([]);
  const [isStartingExport, setIsStartingExport] = useState(false);

  const handleOpenDownloadModal = () => {
    setShowDownloadModal(true);
  };

  const handleToggleTemplate = (templateId) => {
    setSelectedTemplates(prev => {
      if (prev.includes(templateId)) {
        return prev.filter(id => id !== templateId);
      }
      return [...prev, templateId];
    });
  };

  const handleToggleAll = () => {
    if (selectedTemplates.length === filteredTemplates.length) {
      setSelectedTemplates([]);
    } else {
      setSelectedTemplates(filteredTemplates.map(t => t.template_id));
    }
  };

  const handleDownload = async () => {
    try {
      setIsStartingExport(true);

      // Validate userCustomizationsHash
      if (!userCustomizationsHash) {
        console.error('userCustomizationsHash is empty or undefined:', userCustomizationsHash);
        alert('User customizations not loaded yet. Please wait a moment and try again.');
        setIsStartingExport(false);
        return;
      }

      // Transform selected template IDs into array of {templateId, format} objects
      const templates = selectedTemplates.map(templateId => {
        const template = filteredTemplates.find(t => t.template_id === templateId);

        return {
          templateId,
          templateName: template?.template_title,
          projectId: template?.internal_project_id,
          format: formatMap[template?.media_type?.media_type_id] || 'png'
        };
      });

      console.log({
        templates,
        customizations_hash: userCustomizationsHash,
      });

      const response = await DSInstance.post('/template-builder/templates/export', {
        templates,
        customizations_hash: userCustomizationsHash,
      });

      if (response.data && response.data.jobs) {
        setExportJobs(response.data.jobs);
        setShowDownloadModal(false);
        setSelectedTemplates([]);
        setShowProgressModal(true);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error starting export:', error);
      alert('Error starting export. Please try again.');
    } finally {
      setIsStartingExport(false);
    }
  };

  return (
    <div className="w-full">
      {/* Secondary category navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {foundSecondaryCategory()?.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                resetSelectedFilters(true);
                handleSelectSecondaryCategory(item.id);
              }}
              className={`py-2 px-1 border-b-2 font-semibold text-sm whitespace-nowrap ${
                selectedSecondaryCategory === item.id
                  ? 'border-cyan-500 text-neutral-800'
                  : 'border-transparent text-neutral-600 hover:text-neutral-700 hover:border-gray-300'
              }`}
            >
              {item.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Download Button */}
      <div className="mb-6 flex items-center justify-start">
        <span className="font-semibold text-md mr-3">Click here if you want to download the templates using your default customizations</span>

        <Button
          onClick={handleOpenDownloadModal}
          className="whitespace-nowrap scale-[0.9]"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download Templates
        </Button>
      </div>

      <div className="bg-white p-6 rounded-md shadow-md overflow-hidden">
        {/* Search Bar */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>
        </div>

        <Divider />

        {/* Templates Grid */}
        {filteredTemplates.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredTemplates.map((template) => (
              <TemplateCard
                key={template.template_id}
                template={template}
                onCustomize={onCustomize}
                onPreview={onPreview}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-2">No templates found</div>
            <div className="text-gray-500 text-sm">Try adjusting your search or filter criteria</div>
          </div>
        )}
      </div>

      <TemplateSelectionModal
        isOpen={showDownloadModal}
        onClose={() => {
          setShowDownloadModal(false);
          setSelectedTemplates([]);
        }}
        templates={filteredTemplates}
        selectedTemplates={selectedTemplates}
        onToggleTemplate={handleToggleTemplate}
        onToggleAll={handleToggleAll}
        onDownload={handleDownload}
        isStartingExport={isStartingExport}
      />

      <ExportProgressModal
        isOpen={showProgressModal}
        onClose={() => {
          setShowProgressModal(false);
          setExportJobs([]);
        }}
        initialJobs={exportJobs}
      />
    </div>
  );
};

export default DefaultTemplates;